import { useQuery } from '@tanstack/react-query'
import * as dashboardApi from '../../api/dashboard'
import Card from '../../components/common/Card'
import Badge from '../../components/common/Badge'
import Spinner from '../../components/common/Spinner'
import { 
  DocumentTextIcon, 
  UserGroupIcon, 
  UsersIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

/**
 * Dashboard Page
 * Overview with stats cards and recent activity
 */
export default function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardApi.getStats,
  })

  const { data: recentActivity } = useQuery({
    queryKey: ['recent-activity'],
    queryFn: () => dashboardApi.getRecentActivity(10),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">Overview of your contract management system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Contracts */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Contracts</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {stats?.contracts?.total || 0}
              </p>
            </div>
            <div className="bg-primary-100 rounded-lg p-3">
              <DocumentTextIcon className="h-8 w-8 text-primary-600" />
            </div>
          </div>
        </Card>

        {/* Pending Signatures */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Signatures</p>
              <p className="mt-2 text-3xl font-bold text-yellow-600">
                {stats?.contracts?.by_status?.pending || 0}
              </p>
            </div>
            <div className="bg-yellow-100 rounded-lg p-3">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </Card>

        {/* Active Trainers */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Trainers</p>
              <p className="mt-2 text-3xl font-bold text-green-600">
                {stats?.trainers?.with_active_contract || 0}
              </p>
            </div>
            <div className="bg-green-100 rounded-lg p-3">
              <UsersIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </Card>

        {/* Total Teams */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Teams</p>
              <p className="mt-2 text-3xl font-bold text-blue-600">
                {stats?.teams?.total || 0}
              </p>
            </div>
            <div className="bg-blue-100 rounded-lg p-3">
              <UserGroupIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Contract Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Contract Status</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-3">
              <StatusRow 
                label="Signed" 
                count={stats?.contracts?.by_status?.signed || 0}
                variant="success"
              />
              <StatusRow 
                label="Pending" 
                count={stats?.contracts?.by_status?.pending || 0}
                variant="warning"
              />
              <StatusRow 
                label="Draft" 
                count={stats?.contracts?.by_status?.draft || 0}
                variant="info"
              />
              <StatusRow 
                label="Expired" 
                count={stats?.contracts?.by_status?.expired || 0}
                variant="danger"
              />
            </div>
          </Card.Body>
        </Card>

        {/* Recent Activity */}
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          </Card.Header>
          <Card.Body>
            {recentActivity && recentActivity.length > 0 ? (
              <div className="space-y-4">
                {recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-primary-600 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {formatActivityMessage(activity)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No recent activity</p>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Quick Stats */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Quick Overview</h2>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600">Expiring Soon (30 days)</p>
              <p className="mt-1 text-2xl font-bold text-orange-600">
                {stats?.expiring_soon?.next_30_days || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Trainers Without Contract</p>
              <p className="mt-1 text-2xl font-bold text-red-600">
                {stats?.trainers?.without_contract || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Teams Without Trainer</p>
              <p className="mt-1 text-2xl font-bold text-purple-600">
                {stats?.teams?.without_trainer || 0}
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

// Helper component for status rows
function StatusRow({ label, count, variant }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Badge variant={variant}>{label}</Badge>
      </div>
      <span className="text-lg font-semibold text-gray-900">{count}</span>
    </div>
  )
}

// Format activity log message
function formatActivityMessage(activity) {
  const { action, entity_type, details, user } = activity;
  
  // Build user prefix
  const userPrefix = user
    ? `${user.first_name} ${user.last_name} `
    : 'System ';
  
  // Build entity description
  let entityDesc = '';
  switch (entity_type) {
    case 'contract':
      entityDesc = details?.contract_title || `Contract #${activity.entity_id}`;
      break;
    case 'trainer':
      entityDesc = details?.trainer_name || `Trainer #${activity.entity_id}`;
      break;
    case 'team':
      entityDesc = details?.team_name || `Team #${activity.entity_id}`;
      break;
    case 'payment':
      entityDesc = details?.contract_title 
        ? `payment for ${details.contract_title}`
        : `Payment #${activity.entity_id}`;
      break;
    case 'signing_link':
      entityDesc = details?.contract_title || 'signing link';
      break;
    default:
      entityDesc = entity_type;
  }
  
  // Build action verb
  let actionVerb = '';
  switch (action) {
    case 'create':
    case 'contract_created':
      actionVerb = 'created';
      break;
    case 'update':
    case 'contract_updated':
      actionVerb = 'updated';
      break;
    case 'delete':
    case 'contract_deleted':
      actionVerb = 'deleted';
      break;
    case 'sign':
    case 'contract_signed':
      actionVerb = 'signed';
      break;
    case 'send':
    case 'signing_link_sent':
      actionVerb = 'sent';
      break;
    case 'link':
      actionVerb = 'linked';
      break;
    case 'unlink':
      actionVerb = 'unlinked';
      break;
    case 'mark_paid':
      actionVerb = 'marked as paid';
      break;
    default:
      actionVerb = action;
  }
  
  return `${userPrefix}${actionVerb} ${entityDesc}`;
}